import { useMemo } from 'react'
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_PaginationState,
  type MRT_SortingState,
  useMaterialReactTable,
} from 'material-react-table'
import { Box, IconButton, Tooltip } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import ImageIcon from '@mui/icons-material/Image'
import { pb } from '../../../pocketbase/Pocketbase'

interface MetadataItem {
  id: string
  name: string
  image?: string
}

interface MetadataTableProps<T extends MetadataItem> {
  data: T[]
  isLoading: boolean
  pagination: MRT_PaginationState
  rowCount: number
  onPaginationChange: (updater: MRT_PaginationState | ((old: MRT_PaginationState) => MRT_PaginationState)) => void
  onCreate: (values: T) => Promise<void>
  onUpdate: (values: T) => Promise<void>
  onDelete: (row: MRT_Row<T>) => void
  onUploadImage?: (row: MRT_Row<T>) => void
  entityName: string // e.g., "Brand" or "Note"
  columnHeader: string // e.g., "Brand Name" or "Note Name"
  collectionName: string // PocketBase collection name for image URLs
  customToolbarActions?: React.ReactNode // Optional custom actions in the toolbar
  searchValue: string
  onSearchChange: (value: string) => void
  sorting?: MRT_SortingState
  onSortingChange?: (updater: MRT_SortingState | ((old: MRT_SortingState) => MRT_SortingState)) => void
}

export function MetadataTable<T extends MetadataItem>({
  data,
  isLoading,
  pagination,
  rowCount,
  onPaginationChange,
  onCreate,
  onUpdate,
  onDelete,
  onUploadImage,
  entityName,
  columnHeader,
  collectionName,
  customToolbarActions,
  searchValue,
  onSearchChange,
  sorting,
  onSortingChange,
}: MetadataTableProps<T>) {
  const getImageUrl = (row: T) => {
    if (row.image) {
      return pb.files.getURL({ id: row.id, collectionName }, row.image, { thumb: '100x100' })
    }
    return null
  }

  const columns = useMemo<MRT_ColumnDef<T>[]>(
    () => [
      {
        accessorKey: 'image',
        header: 'Image',
        size: 80,
        enableEditing: false,
        enableSorting: true,
        Cell: ({ row }) => {
          const imageUrl = getImageUrl(row.original)
          return imageUrl ? (
            <img
              src={imageUrl}
              alt={row.original.name}
              style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
            />
          ) : (
            <div style={{ width: 40, height: 40, backgroundColor: '#f0f0f0', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>N/A</div>
          )
        },
      },
      {
        accessorKey: 'id',
        header: 'ID',
        enableEditing: false,
        enableSorting: false,
      },
      {
        accessorKey: 'name',
        header: columnHeader,
        enableSorting: false,
        muiEditTextFieldProps: {
          required: true,
        },
      },
    ],
    [columnHeader, collectionName],
  )

  const table = useMaterialReactTable({
    columns,
    data,
    createDisplayMode: 'row',
    editDisplayMode: 'row',
    enableEditing: true,
    getRowId: (row) => row.id,
    manualPagination: true,
    rowCount,
    state: {
      isLoading,
      pagination,
      globalFilter: searchValue,
      sorting: sorting ?? [],
    },
    onPaginationChange,
    onGlobalFilterChange: onSearchChange,
    onSortingChange,
    manualFiltering: true,
    manualSorting: true,
    enableColumnFilters: false,
    enableColumnActions: false,
    enableSorting: true,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableGlobalFilter: true,
    onCreatingRowSave: async ({ values, table }) => {
      await onCreate(values as T)
      table.setCreatingRow(null)
    },
    onEditingRowSave: async ({ values, table }) => {
      await onUpdate(values as T)
      table.setEditingRow(null)
    },
    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: 'flex', gap: '0.5rem' }}>
        <Tooltip title="Edit">
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        {onUploadImage && (
          <Tooltip title="Upload Image">
            <IconButton onClick={() => onUploadImage(row)}>
              <ImageIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Delete">
          <IconButton color="error" onClick={() => onDelete(row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    renderTopToolbarCustomActions: ({ table }) => (
      <div className="flex gap-2">
        <button
          onClick={() => table.setCreatingRow(true)}
          className="px-4 py-2 cursor-pointer border-none rounded bg-black text-white hover:bg-gray-800 transition-colors"
        >
          Add New {entityName}
        </button>
        {customToolbarActions}
      </div>
    ),
    muiTableContainerProps: {
      sx: {
        maxHeight: '100%',
        height: '100%',
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        border: 'none',
      },
    },
  })

  return <MaterialReactTable table={table} />
}
